import axios from 'axios';
import moment from 'moment';
import {getAxiosConfig, getQueryString} from '../shared';
import {
    activeSortedUsers,
    combineBroadcastAndTemplate,
    extractUnlayerBroadcastPayload,
    transformEmailTemplateToBroadcastTemplate,
} from '@/components/broadcast/utils/emailBroadcast.util';
import {
    BROADCAST_FILTER_VALUES,
    BROADCAST_STATUS,
    CONTACT_TIME_ZONE_OPTION,
    EMAIL_EDITORS,
    REQUEST_PAYLOAD_DATE_FORMAT,
    UNLAYER_BROADCAST_COMMAND,
    UNLAYER_TEMPLATE_COMMAND,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import {STEPS} from '@/components/unlayer/constants/editor.constants';

const EMAIL_BROADCAST_BASE_URL = '/app/email/broadcast';

export const UNLAYER_BROADCAST_BASE_URL = '/app/unlayerBroadcast';

export const getAllEmailBroadcasts = ({
    offset = 0,
    limit = 10,
    search = '',
    filter = null,
}) => {
    try {
        return axios.post('/app/emailBroadcastController/all', {
            offset,
            limit,
            search,
            filter,
        });
    } catch (error) {
        throw new Error('Could not get the email broadcasts.');
    }
};

export const stopEmailBroadcast = (mailBatchId) => {
    try {
        return axios.post(`${EMAIL_BROADCAST_BASE_URL}/report/stopMailBatch/${mailBatchId}`);
    } catch (error) {
        throw new Error('Could not stop the email broadcast.');
    }
};

export const resumeEmailBroadcast = (mailBatchId) => {
    try {
        return axios.patch(`/app/emailBroadcastController/resumeBroadcast/${mailBatchId}`);
    } catch (error) {
        throw new Error('Could not resume the email broadcast.');
    }
};

export const deleteDraft = (broadcastId) => {
    try {
        return axios.delete(`${EMAIL_BROADCAST_BASE_URL}/draft/${broadcastId}`);
    } catch (error) {
        throw new Error('Could not delete the email broadcast.');
    }
};

export const copyBroadcast = (params) => {
    try {
        return axios.post(`${EMAIL_BROADCAST_BASE_URL}/fromPreviouslySent`, params);
    } catch (error) {
        throw new Error('Could not copy the email broadcast.');
    }
};

export const rescheduleBroadcast = (mailBatchId, scheduledDateTime, timeZoneId) => {
    try {
        const useContactTimeZone = timeZoneId === CONTACT_TIME_ZONE_OPTION;
        const timeZoneToSend = !useContactTimeZone ? timeZoneId : null;

        return axios.post(`${EMAIL_BROADCAST_BASE_URL}/report/rescheduleMailBatch/${mailBatchId}`, {
            scheduledDateTime,
            useContactTimeZone,
            timeZoneId: timeZoneToSend,
        });
    } catch (error) {
        throw new Error('Could not reschedule the email broadcast.');
    }
};

export const getBroadcastTimeZones = () => {
    try {
        return axios.get(`${EMAIL_BROADCAST_BASE_URL}/timeZones`);
    } catch (error) {
        throw new Error('Error retrieving timezones.');
    }
};

export const completeApplePrivacyGuideEmailBroadcastPage = () => {
    try {
        return axios.put('/app/showHideEmailOpens/dismissAppleFtuGuideBroadcastsPage', JSON.stringify(true), getAxiosConfig());
    } catch (error) {
        throw new Error('Could not permanently dismiss the Apple Privacy guide');
    }
};

export const saveShowHideOpenRatesUserPreference = (boolean) => {
    try {
        return axios.put('/app/showHideEmailOpens/toggleShowHideEmailOpens', JSON.stringify(boolean), getAxiosConfig());
    } catch (error) {
        throw new Error('Could not save the show/hide email open rates setting');
    }
};

export const getIsEmailLockedDown = async () => {
    try {
        const { data } = await axios.get('/app/broadcast/checkEmailLockdown');

        return data;
    } catch (error) {
        throw new Error('Could not get email lockdown state');
    }
};

export const copyUnlayerBroadcast = async (broadcastId) => {
    try {
        const existingBroadcast = await getUnlayerEmailBroadcastById(broadcastId);

        const { externalTemplateId } = existingBroadcast.emailTemplate;

        let existingTemplate;
        let newTemplate;

        if (externalTemplateId && externalTemplateId !== 0) {
            existingTemplate = await getUnlayerTemplate({ id: externalTemplateId });

            newTemplate = await createUnlayerTemplate({
                title: existingTemplate.title,
                html: existingTemplate.html,
                builderJson: existingTemplate.builderJson,
                templateSource: 'EMAIL_BROADCAST',
                templateStatus: BROADCAST_STATUS.DRAFT,
                templateMedia: 'EMAIL',
            });
        }

        const newBroadcast = await createUnlayerEmailBroadcast({
            emailTemplate: {
                bardJson: null,
                title: existingBroadcast.emailTemplate.title,
                from: existingBroadcast.emailTemplate.from,
                htmlBody: existingBroadcast.emailTemplate.htmlBody,
                previewText: existingBroadcast.emailTemplate.previewText,
                subject: existingBroadcast.emailTemplate.subject,
                editor: existingBroadcast.emailTemplate.editor,
                externalTemplateId: newTemplate ? newTemplate.id : externalTemplateId,
                toAddress: existingBroadcast.emailTemplate.toAddress,
                replyToAddress: existingBroadcast.emailTemplate.replyToAddress,
            },
        });

        await unlayerBroadcastAddMultipleRecipients(newBroadcast.id, existingBroadcast.recipients);

        await updateUnlayerEmailBroadcast({
            ...newBroadcast,
            scheduleDate: null,
            timeZoneId: '',
            useContactTimeZone: true,
        });

        await updateUnlayerTemplate( { id: newBroadcast.id, templateId: newTemplate.id, CONTENT: { htmlBody: newTemplate.html, design: newTemplate.builderJson } } );

        return newBroadcast.id;
    } catch (err) {
        throw new Error('Could not copy broadcast');
    }
};

export const deleteUnlayerDraft = (broadcastId) => {
    try {
        return axios.delete(`${UNLAYER_BROADCAST_BASE_URL}/${broadcastId}/delete`);
    } catch (e) {
        throw new Error('Could not delete broadcast');
    }
};

export const unlayerBroadcastsGetUsers = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/users`);

        return data;
    } catch (error) {
        throw new Error('Could not get app users');
    }
};

export const fetchActiveUsers = async () => {
    const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/users`);

    return activeSortedUsers(data);
};

const createUnlayerEmailBroadcast = async (template) => {
    try {
        const { data } = await axios.post(
            UNLAYER_BROADCAST_BASE_URL,
            template,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not create unlayer broadcast');
    }
};

export const getUnlayerBroadcastReportByBroadcastId = async (broadcastId) => {
    try {
        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/broadcastReport/${broadcastId}`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not fetch unlayer broadcast report');
    }
};

const getUnlayerEmailBroadcastById = async (broadcastId) => {
    try {
        if (!broadcastId) throw new Error();

        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/${broadcastId}`,
            getAxiosConfig(),
        );

        if (data?.scheduleDate) {
            data.scheduleDate = moment(data.scheduleDate).format(REQUEST_PAYLOAD_DATE_FORMAT);
        }

        return data;
    } catch (error) {
        throw new Error('Could not fetch unlayer broadcast');
    }
};

export const recalculateEmailBroadcastRecipientStatisticsById = async (broadcastId) => {
    try {
        if (!broadcastId) throw new Error();

        const { data } = await axios.post(
                `${UNLAYER_BROADCAST_BASE_URL}/recalculateBroadcastRecipients/${broadcastId}`,
                getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not recalculate unlayer broadcast recipients');
    }
};

const updateUnlayerEmailBroadcast = async (broadcast) => {
    try {
        const {
            emailTemplate,
            id: broadcastId,
            scheduleDate,
            timeZoneId,
            useContactTimeZone,
        } = broadcast;

        if (!broadcastId) throw new Error();

        const payload = {
            emailTemplate: {
                externalTemplateId: emailTemplate.externalTemplateId,
                from: emailTemplate.from,
                htmlBody: emailTemplate.htmlBody,
                previewText: emailTemplate.previewText,
                subject: emailTemplate.subject,
                title: emailTemplate.title,
                toAddress: emailTemplate.toAddress,
                replyToAddress: emailTemplate.replyToAddress,
            },
            scheduleDate: moment(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT).toISOString(),
            timeZoneId,
            useContactTimeZone,
        };
        const { data } = await axios.put(
            `${UNLAYER_BROADCAST_BASE_URL}/${broadcastId}`,
            payload,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not update unlayer broadcast');
    }
};

const createUnlayerTemplate = async (template) => {
    try {
        const { data } = await axios.post(
            `${UNLAYER_BROADCAST_BASE_URL}/template`,
            template,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not create unlayer broadcast template');
    }
};

const getUnlayerTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/template/${templateId}`,
            getAxiosConfig(),
        );


        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcast template');
    }
};

const updateUnlayerTemplate = async (email) => {
    try {
        if (!email.templateId) throw new Error();

        const payload = {
            design: JSON.stringify(email[STEPS.CONTENT].design),
            html: email[STEPS.CONTENT].htmlBody,
            templateSource: 'EMAIL_BROADCAST',
            sourceId: email.id,
        };

        const { data } = await axios.put(
            `${UNLAYER_BROADCAST_BASE_URL}/template/${email.templateId}`,
            payload,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not update unlayer broadcast template');
    }
};

export const getAllUnlayerDraftBroadcastTemplates = async (params = {}) => {
    try {
        const queryString = getQueryString({
            ...params,
            editor: EMAIL_EDITORS.UNLAYER,
            status: BROADCAST_FILTER_VALUES.DRAFT,
        });

        const { data } = await axios.get(
            encodeURI(`${UNLAYER_BROADCAST_BASE_URL}/${queryString}`),
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not fetch unlayer draft broadcasts');
    }
};

export const getAllUnlayerSentBroadcastTemplates = async (params = {}) => {
    try {
        const queryString = getQueryString({
            ...params,
            editor: EMAIL_EDITORS.UNLAYER,
            status: BROADCAST_FILTER_VALUES.SENT,
        });

        const { data } = await axios.get(
            encodeURI(`${UNLAYER_BROADCAST_BASE_URL}/${queryString}`),
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not fetch unlayer sent broadcasts');
    }
};

export const unlayerBroadcastsGetRecipientCount = async ({ id: broadcastId, category, type }) => {
    try {
        if (!broadcastId || !(category || type)) throw new Error();

        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/autocomplete/${category || type}/${broadcastId}/count`,
        );

        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcast recipient count');
    }
};

export const unlayerBroadcastsAddRecipientCriteria = async ({ broadcastId, recipient }) => {
    try {
        if (!broadcastId || !recipient.id) throw new Error();

        recipient.id = +recipient.id;

        const { data } = await axios.post(
            `${UNLAYER_BROADCAST_BASE_URL}/recipientCriteria/${broadcastId}/add`,
            recipient,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not add recipient to unlayer broadcast');
    }
};

export const unlayerBroadcastAddMultipleRecipients = async (broadcastId, recipientList) => {
    try {
        // just strips unnecessary fields
        const toRecipientPayload = ({
            id,
            category,
            label,
            name,
            type,
        }) => {
            return {
                id: `${id}`,
                category: category || type,
                label: label || name,
            };
        };

        // due to the way the BE currently processes these requests, the below calls must be sequential
        for (const recipient of recipientList) {
            /* eslint-disable-next-line no-await-in-loop */
            await unlayerBroadcastsAddRecipientCriteria({
                broadcastId,
                recipient: toRecipientPayload(recipient),
            });
        }
    } catch (e) {
        throw new Error('Could not add recipients to broadcast');
    }
};

export const unlayerBroadcastsRemoveRecipientCriteria = async ({ broadcastId, recipient }) => {
    try {
        if (!broadcastId || !recipient.id) throw new Error();

        recipient.id = +recipient.id;

        const { data } = await axios.post(
            `${UNLAYER_BROADCAST_BASE_URL}/recipientCriteria/${broadcastId}/remove`,
            recipient,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not remove recipient from unlayer broadcast');
    }
};

export const unlayerBroadcastsSearchEmailRecipients = async ({ query }) => {
    try {
        const strategy = 'savedsearch-tags-contacts';

        const { data } = await axios.get(
            encodeURI(`${UNLAYER_BROADCAST_BASE_URL}/autocomplete/find/?strategy=${strategy}&q=${query}`),
        );

        return data.autocompleteResponseItemList;
    } catch (error) {
        throw new Error('Could not get recipients');
    }
};

export const getAllUnlayerBroadcastsSavedTemplates = async () => {
    try {
        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/savedTemplates`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcasts saved templates');
    }
};

export const getAllUnlayerBroadcastsGlobalTemplates = async () => {
    try {
        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/globalTemplates`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcasts global templates');
    }
};

export const getUnlayerBroadcastsGlobalTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/globalTemplate/${templateId}`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcasts global template');
    }
};

export const getUnlayerBroadcastWithTemplateById = async (broadcastId) => {
    try {
        const broadcast = await getUnlayerEmailBroadcastById(broadcastId);
        const template = await getUnlayerTemplate({ id: broadcast.emailTemplate.externalTemplateId });

        const combinedBroadcastAndTemplate = combineBroadcastAndTemplate(broadcast, template);

        return combinedBroadcastAndTemplate;
    } catch (error) {
        throw new Error('Could not get unified broadcast');
    }
};

export const createUnlayerBroadcastWithTemplate = async (listId) => {
    try {
        const emailBroadcastCommand = { ...UNLAYER_BROADCAST_COMMAND };
        const unlayerTemplateCommand = { ...UNLAYER_TEMPLATE_COMMAND };

        const newTemplate = await createUnlayerTemplate(unlayerTemplateCommand);
        
        emailBroadcastCommand.emailTemplate.externalTemplateId = newTemplate.id;
        emailBroadcastCommand.emailTemplate.htmlBody = newTemplate.html;
        emailBroadcastCommand.emailTemplate.listId = listId;
        emailBroadcastCommand.emailTemplate.title = newTemplate.title;

        const newBroadcast = await createUnlayerEmailBroadcast(emailBroadcastCommand);

        await updateUnlayerTemplate( { id: newBroadcast.id, templateId: newTemplate.id, CONTENT: { htmlBody: newTemplate.html, design: newTemplate.builderJson } } );

        return combineBroadcastAndTemplate(newBroadcast, newTemplate);
    } catch (error) {
        throw new Error('Could not create unified broadcast');
    }
};

export const updateUnlayerBroadcastWithTemplate = async (email) => {
    try {
        if (!email.id || !email.templateId) {
            throw new Error();
        }

        const updatedTemplate = await updateUnlayerTemplate(email);

        const updateBroadcastPayload = extractUnlayerBroadcastPayload(email);

        updateBroadcastPayload.emailTemplate.htmlBody = updatedTemplate.html;
        const updatedBroadcast = await updateUnlayerEmailBroadcast(updateBroadcastPayload);

        const combinedBroadcastAndTemplate = combineBroadcastAndTemplate(updatedBroadcast, updatedTemplate);

        return combinedBroadcastAndTemplate;
    } catch (error) {
        throw new Error('Could not update unified broadcast');
    }
};

export const getUnlayerBroadcastGlobalTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const template = await getUnlayerBroadcastsGlobalTemplate({ id: templateId });

        const transformedTemplate = transformEmailTemplateToBroadcastTemplate(template);

        return transformedTemplate;
    } catch (error) {
        throw new Error('Could not get unified broadcast global template');
    }
};

export const getUnlayerBroadcastSavedTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const template = await getUnlayerTemplate({ id: templateId });
        const transformedTemplate = transformEmailTemplateToBroadcastTemplate(template);

        return transformedTemplate;
    } catch (error) {
        throw new Error('Could not get unified broadcast saved template');
    }
};

export const getUnlayerBroadcastTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const template = await getUnlayerTemplate({ id: templateId });

        const transformedTemplate = transformEmailTemplateToBroadcastTemplate(template);

        return transformedTemplate;
    } catch (error) {
        throw new Error('Could not get unified broadcast template');
    }
};

export const getLocalizationInfo = async (params) => {
    try {
        const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/localizationInfo`, { params });

        return data;
    } catch (error) {
        throw new Error('Could not get localization information');
    }
};

export const getMarketingSettingsInformation = async() => {
    try {
        const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/marketingSettingsInformation`);

        return data;
    } catch (error) {
        throw new Error('Could not get marketing settings information');
    }
};

export const saveMarketingSettingsInformation = async({ profile }) => {
    try {
        const {
            companyStreetAddress1,
            companyCountry,
            companyCity,
            companyPostalCode,
        } = profile;

        if (!companyStreetAddress1 || !companyCountry || !companyCity || !companyPostalCode) {
            throw new Error('Missing CAN-SPAM information. Please reload the page and try again.');
        }

        const updatedCountryAndRegionNames = await getUpdatedCountryAndRegionNames(profile.companyCountry, profile.companyState);

        const profileCopy = Object.assign({}, profile);

        profileCopy.companyCountry = updatedCountryAndRegionNames.country;
        profileCopy.companyState = updatedCountryAndRegionNames.state;

        const params = new URLSearchParams();

        params.append('companyName', profileCopy.companyName);
        params.append('companyStreetAddress1', profileCopy.companyStreetAddress1);
        params.append('companyStreetAddress2', profileCopy.companyStreetAddress2);
        params.append('companyCity', profileCopy.companyCity);
        params.append('companyState', profileCopy.companyState);
        params.append('companyPostalCode', profileCopy.companyPostalCode);
        params.append('companyCountry', profileCopy.companyCountry);
        params.append('companyPhone', profileCopy.companyPhone);

        const { data } = await axios.post(
            '/app/canSpam/save',
            params,
            getAxiosConfig({
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            }),
        );

        return data;
    } catch (error) {
        throw new Error('Could not save marketing settings information');
    }
};

export const getCountryOptions = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/countryOptions`);

        return mapToArray(data);
    } catch (error) {
        throw new Error('Could not get country options');
    }
};

export const getStateMapForCountry = async ({ country }) => {
    try {
        if (!country || typeof country !== 'string') { throw new Error('Invalid country code'); }

        const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/${encodeURI(country)}/states`);

        return mapToArray(data);
    } catch (error) {
        throw new Error('Could not get state options');
    }
};

const getUpdatedCountryAndRegionNames = async (country, state = '') => {
    const params = {
        countryIso3: country,
    };

    if (state) { params.regionIso3 = state; }

    const queryString = getQueryString({
        ...params,
    });

    try {
        const { data } = await axios.get(
            `${UNLAYER_BROADCAST_BASE_URL}/countryAndRegionNameForISO3/${queryString}`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get updated country and region names');
    }
};

export const getCustomDomains = async () => {
    try {
        const { data } = await axios.get(
            '/app/emailAuthorization/dkimSettings',
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get custom domains');
    }
};

export const getBroadcastPreviewHtml = async ({ broadcastId }) => {
    const { data } = await axios.get(`${UNLAYER_BROADCAST_BASE_URL}/${broadcastId}/preview`);

    return data;
};

export const sendBroadcast = ({ broadcastId, schedule }) => {
    return axios.post(`${UNLAYER_BROADCAST_BASE_URL}/${broadcastId}/${schedule ? 'sendAsScheduled' : 'sendNow'}`, schedule);
};

const mapToArray = (map) => {
    return Object.keys(map).map((key) => {
        return { value: key, label: map[key] };
    });
};

export const createList = async ({ reportClass, reportStateId, filterId }) => {
    try {
        const { data: listId } = await axios.post('/app/emailBroadcast/createList', null, {
            params: {
                reportClass,
                reportStateId,
                filterId,
                fromQuickSearchBoolean: false,
                prioritizeFilterId: !!filterId,
            },
        });

        return listId;
    } catch (error) {
        throw new Error('Could not create list');
    }
};

export const unlayerBroadcastsGetRecipientCountV2 = async ({ id: broadcastId, category, type }) => {
    try {
        if (!broadcastId || !(category || type)) throw new Error();

        const { data } = await axios.get(
                `${UNLAYER_BROADCAST_BASE_URL}/v2/autocomplete/${category || type}/${broadcastId}/count`,
        );

        return data;
    } catch (error) {
        throw new Error('Could not get unlayer broadcast recipient count');
    }
};
